import {
    Box,
    FormControl,
    FormControlProps,
    InputLabel,
    InputLabelProps,
    MenuItem,
    Select as MuiSelect,
    OutlinedInput,
    SelectChangeEvent,
    TextField,
    Typography
} from "@mui/material";
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputSelectProps } from "./types/FieldTypes";

export interface ISelectItem<TId = any> {
    id?: TId;
    name?: string | ReactNode;
    description?: string;
}

export interface ISelectProps<TId = any> extends Omit<InputSelectProps<TId>, 'margin' | 'onChange'> {
    nullOption?: boolean;
    label?: string;
    FormControlProps?: FormControlProps;
    onChange?: (event: SelectChangeEvent<any>, child: React.ReactNode, selectedItem: any) => void;
    minValue?: number,
    minLabel?: string,
    step?: number,
    maxValue?: number,
    maxLabel: string,
    helperText?: ReactNode,
    margin: FormControlProps['margin'],
    InputLabelProps?: InputLabelProps,
    defaultValue?: number
}

const InputDropdownWithNumberField: FC<ISelectProps> = ({
    FieldInputProps: { state: { propertyPath, value }, actions: { update, blur, focus, touch } },
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    minValue,
    minLabel,
    step,
    maxValue,
    maxLabel,
    label,
    margin,
    fullWidth,
    nullOption,
    onChange,
    defaultValue,
    ...rest
}) => {
    const [showTextInput, setShowTextInput] = useState<boolean>(false);

    useEffect(() => {
        if (value != undefined && value >= (maxValue ?? 0)) { 
            setShowTextInput(true);
        }
    }, [value]);

    const numberInputRef = useRef<HTMLInputElement>(null);

    const options: ISelectItem[] = useMemo(() => {
        if (minValue == undefined || maxValue == undefined) return [];
        
        const tempOptions: ISelectItem[] = [];
        const increment = step ?? 1;

        for (let i = minValue; i <= maxValue; i += increment) {
            if (i === minValue) {
                tempOptions.push({
                    id: i,
                    name: minLabel ?? i.toString()
                });
            } else if (i === maxValue) {
                tempOptions.push({
                    id: i,
                    name: maxLabel ?? i.toString()
                });
            } else {
                tempOptions.push({
                    id: i,
                    name: i.toString()
                });
            }
        }
        return tempOptions;
    }, [minValue, minLabel, step, maxValue, maxLabel]);

    const lastOptionId = useMemo(() => { 
        return options.length ? options[options.length - 1].id?.toString() : undefined
    }, [options]);

    const onChangeCombined = useCallback((event: SelectChangeEvent<{ name: string | undefined; value: number; }>, child: React.ReactNode) => {
        const selectedItem = options?.find(x => x.id?.toString() === event.target.value?.toString());
        const selectedId = event.target.value;

        if (onChange) {
            onChange(event, child, selectedItem);
        }

        if (selectedId === lastOptionId) {
            update(undefined, true, true, true);
            setShowTextInput(true);
            return;
        }
        setShowTextInput(false);
        update(selectedId, true, true, true);
    }, [update, lastOptionId]);

    const onNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === "") {
            update(undefined, true, true, true);
            return;
        }

        // Allow only digits
        if (!/^\d+$/.test(value)) {
            return;
        }

        const newValue = Number(value);

        update(newValue, true, true, true);
    }, [update, maxValue]);
    
    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    return (
        <Box>
            <FormControl margin={margin} fullWidth={fullWidth}>
                <InputLabel id={`${propertyPath}-label`}>
                    {label}
                </InputLabel>

                <MuiSelect
                    {...rest as any}
                    labelId={`${propertyPath}-label`}
                    defaultValue={defaultValue}
                    value={showTextInput ? maxValue : value ?? ""}
                    label={label}
                    onChange={onChangeCombined}
                    input={<OutlinedInput label={label} />}
                    inputProps={{
                        ...inputProps,
                        onBlur: onBlurCombined,
                        onFocus: onFocusCombined
                    }}
                >
                    {(nullOption !== false) && (
                        <MenuItem value="">
                            &nbsp;
                        </MenuItem>
                    )}

                    {options.map((item) => (
                        <MenuItem
                            key={item.id?.toString()}
                            value={item.id?.toString()}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%"
                                }}
                            >
                                {item.name}
                                {item.description && (
                                    <Typography variant="caption">
                                        {item.description}
                                    </Typography>
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </MuiSelect>
            </FormControl>

            {showTextInput && (
                <TextField
                    type="text" 
                    label="Enter value"
                    margin="dense"
                    fullWidth
                    value={value ?? ""}
                    onChange={onNumberChange}
                    onFocus={onFocusCombined}
                    onBlur={onBlurCombined}
                    inputRef={numberInputRef}
                    inputProps={{
                        pattern: /^[0-9]*$/,
                        inputMode: "numeric",
                        min: 0, 
                    }}
                />
            )}
        </Box>
    );
};

export default InputDropdownWithNumberField;